import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { API } from "aws-amplify";
import { listSellingByDate, listProductsWithoutCustomers } from "../../graphql/queries";
import BarChart from "../../components/chart/bar.jsx";
import DoughnutChart from "../../components/chart/doughnut.jsx";

const SalesTrends = () => {
  const [overlay, setOverlay] = useState(true);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [products, setProducts] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [chartData, setChartData] = useState(null);
  const [doughnutData, setDoughnutData] = useState(null);
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);
  
  const [formStartDate, setFormStartDate] = useState(oneMonthAgo);
  const [formEndDate, setFormEndDate] = useState(today);
  
  const [isDataFetched, setIsDataFetched] = useState(false);

  const getDayDifference = (start, end) => {
    const ONE_DAY = 24 * 60 * 60 * 1000;
    return Math.floor((end.getTime() - start.getTime()) / ONE_DAY) + 1;
  };

  const getUnixTimestamp = (date, isEndOfDay = false) => {
    const adjustedDate = new Date(date);
    if (isEndOfDay) {
      adjustedDate.setHours(23, 59, 59, 999);
    }
    return Math.floor(adjustedDate.getTime() / 1000);
  };

  const handleThisWeek = () => {
    const currentDate = new Date();
    const dayOfWeek = currentDate.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startOfWeek = new Date(currentDate);
    startOfWeek.setHours(0, 0, 0, 0);
    startOfWeek.setDate(startOfWeek.getDate() - mondayOffset);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    setFormStartDate(startOfWeek);
    setFormEndDate(endOfWeek);
  };

  const handlePrevWeek = () => {
    const newStartDate = new Date(formStartDate);
    const newEndDate = new Date(formEndDate);
    newStartDate.setDate(newStartDate.getDate() - 7);
    newEndDate.setDate(newEndDate.getDate() - 7);
    setFormStartDate(newStartDate);
    setFormEndDate(newEndDate);
  };

  const handleNextWeek = () => {
    const newStartDate = new Date(formStartDate);
    const newEndDate = new Date(formEndDate);
    newStartDate.setDate(newStartDate.getDate() + 7);
    newEndDate.setDate(newEndDate.getDate() + 7);
    setFormStartDate(newStartDate);
    setFormEndDate(newEndDate);
  };

  useEffect(() => {
    let isMounted = true;
    const fetchAllData = async () => {
      const fetchedProducts = await fetchProducts();
      const fetchedData = await fetchData(formStartDate, formEndDate);
      if (isMounted && fetchedProducts && fetchedData.length > 0) {
        setIsDataFetched(true);
      }
    };
    fetchAllData();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (isDataFetched && Object.keys(productMap).length > 0) {
      processProductData(filteredData);
    }
  }, [productMap, isDataFetched]);

  const generateMonthRange = (startDate, endDate) => {
    const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    const months = [];
    while (start <= end) {
      months.push(Math.floor(start.getTime() / 1000));
      start.setMonth(start.getMonth() + 1);
    }
    return months;
  };

  const fetchData = async (startDateObj, endDateObj) => {
    setOverlay(true);
    let allFetchedData = [];
    const startDate = getUnixTimestamp(startDateObj);
    const endDate = getUnixTimestamp(endDateObj);
    const months = generateMonthRange(new Date(startDate * 1000), new Date(endDate * 1000));

    try {
      for (const month of months) {
        let nextTokenData = null;
        do {
          const variables = {
            month,
            startDate,
            endDate,
            limit: 100,
            nextToken: nextTokenData,
          };
          const response = await API.graphql({
            query: listSellingByDate,
            variables,
            authMode: 'AMAZON_COGNITO_USER_POOLS',
          });          
          const items = response.data.listSellingByDate.items || [];
          nextTokenData = response.data.listSellingByDate.nextToken;
          if (items.length > 0) {
            allFetchedData = [...allFetchedData, ...items];
          }
        } while (nextTokenData !== null);
      }
      setAllData(allFetchedData);
      filterDataByDate(allFetchedData, startDateObj, endDateObj);
      return allFetchedData;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setOverlay(false);
    }
  };

  const filterDataByDate = (data, startDateObj, endDateObj) => {
    const startTimestamp = getUnixTimestamp(startDateObj);
    const endTimestamp = getUnixTimestamp(endDateObj, true);
    const filtered = data.filter(
      (item) => item.date >= startTimestamp && item.date <= endTimestamp
    );
    setFilteredData(filtered);
    processChartData(filtered);
    processProductData(filtered);
  };

  const fetchProducts = async () => {
    let nextToken = null;
    const productMapping = {};
    try {
      do {
        const response = await API.graphql({
          query: listProductsWithoutCustomers,
          variables: { limit: 100, nextToken },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        });        
        const products = response.data.listProducts.items;
        nextToken = response.data.listProducts.nextToken;

        products.forEach((product) => {
          productMapping[product.id] = {
            title: product.title,
            price: product.price,
          };
        });
      } while (nextToken);
      setProductMap(productMapping);
      return true;
    } catch (error) {
      console.error("Error fetching products:", error);
      return false;
    }
  };

  const processChartData = (data) => {
    if (data.length === 0) {
      setChartData(null);
      return;
    }
    const labels = data.map((item) =>
      new Date(item.date * 1000).toLocaleDateString()
    );
    const sellingData = data.map((item) => item.selling);
    const feeSalesData = data.map((item) => {
      const customers = JSON.parse(item.customers);
      return customers.length * 200;
    });
    setChartData({
      labels,
      datasets: [
        {
          label: "総売上",
          backgroundColor: "#2B81D6",
          data: sellingData,
        },
        {
          label: "手数料売上",
          backgroundColor: "#FF9F40",
          data: feeSalesData,
        },
      ],
    });
  };

  const processProductData = (data) => {
    if (data.length === 0) {
      setProducts([]);
      setDoughnutData(null);
      return;
    }
    const localProductMap = {};
    data.forEach((item) => {
      const customers = JSON.parse(item.customers);
      customers.forEach((customer) => {
        if (!localProductMap[customer.productID]) {
          localProductMap[customer.productID] = {
            productID: customer.productID,
            price: productMap[customer.productID]?.price || "未設定",
            totalSales: 0,
            feeSales: 0,
          };
        }
        localProductMap[customer.productID].totalSales += item.selling;
        localProductMap[customer.productID].feeSales += 200;
      });
    });
    const productList = Object.values(localProductMap);
    setProducts(productList);
    processDoughnutChart(productList);
  };

  

  const processDoughnutChart = (productList) => {
    if (!productList || productList.length === 0) {
      setDoughnutData(null);
      return;
    }
    const labels = productList.map(
      (product) => productMap[product.productID]?.title || "不明な商品"
    );
    const totalSalesData = productList.map((product) => product.totalSales);
    const feeSalesData = productList.map((product) => product.feeSales);

    setDoughnutData({
      labels,
      datasets: [
        {
          label: "総売上",
          data: totalSalesData,
          backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56"],
        },
        {
          label: "手数料売上",
          data: feeSalesData,
          backgroundColor: ["#FF9F40", "#4BC0C0", "#9966FF"],
        },
      ],
    });
  };

  const dayRange = getDayDifference(formStartDate, formEndDate);
  const shouldShowDoughnut = dayRange < 11;

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Box p={4}>
        {overlay ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h4" mb={3}>
            売上推移: {
              new Set(filteredData.map(item =>
                new Date(item.date * 1000).toDateString()
              )).size
            } 日分
            </Typography>
            <Grid container spacing={2} alignItems="center" justifyContent="center" mb={3}>
              <Grid item>
                <Typography>期間:</Typography>
              </Grid>
              <Grid item>
                <DatePicker
                  value={formStartDate}
                  onChange={(date) => setFormStartDate(date)}
                  label="開始日"
                  format="yyyy-MM-dd"
                />
              </Grid>
              <Grid item>
                <Typography>-</Typography>
              </Grid>
              <Grid item>
                <DatePicker
                  value={formEndDate}
                  onChange={(date) => setFormEndDate(date)}
                  label="終了日"
                  format="yyyy-MM-dd"
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => fetchData(formStartDate, formEndDate)}
                >
                  変更
                </Button>
              </Grid>
              <Grid item>
                <Button variant="contained" onClick={handlePrevWeek}>
                  &lt;
                </Button>
              </Grid>
              <Grid item>
                <Button variant="contained" onClick={handleThisWeek}>
                  今週
                </Button>
              </Grid>
              <Grid item>
                <Button variant="contained" onClick={handleNextWeek}>
                  &gt;
                </Button>
              </Grid>
            </Grid>

            {chartData && <BarChart data={chartData} options={{ responsive: true }} />}

            {shouldShowDoughnut && doughnutData && (
              <Box mt={4}  height="500px">
                <Typography variant="h5" mb={2}>
                  商品別売上比率（外側: 総売上, 内側: 手数料売上）
                </Typography>
                <DoughnutChart
                  data={doughnutData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                      padding: {
                        right: 50
                      }
                    },
                    plugins: {
                      legend: {
                        position: 'right'
                      }
                    }
                  }}
                />
              </Box>
            )}

            <Box mt={4}>
              <Typography variant="h5">商品情報</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>商品名</TableCell>
                    <TableCell>価格（円）</TableCell>
                    <TableCell>総売上</TableCell>
                    <TableCell>手数料売上</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {productMap[product.productID]?.title || "不明な商品"}
                      </TableCell>
                      <TableCell>
                        {product.price !== "未設定" ? `${product.price}円` : "未設定"}
                      </TableCell>
                      <TableCell>{product.totalSales}円</TableCell>
                      <TableCell>{product.feeSales}円</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell><strong>合計</strong></TableCell>
                    <TableCell></TableCell>
                    <TableCell>
                      <strong>
                        {products.reduce((sum, p) => sum + p.totalSales, 0).toLocaleString()}円
                      </strong>
                    </TableCell>
                    <TableCell>
                      <strong>
                        {products.reduce((sum, p) => sum + p.feeSales, 0).toLocaleString()}円
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            <Box mt={4} display="flex" justifyContent="flex-start">
              <Button
                variant="contained"
                color="secondary"
                onClick={() => (window.location.href = "/admin")}
              >
                管理画面トップに戻る
              </Button>
            </Box>
          </>
        )}
      </Box>
    </MuiPickersUtilsProvider>
  );
};

export default SalesTrends;
